import React, { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import SearchInput from '../components/SearchInput';
import DataTable, { Column } from '../components/DataTable';
import Badge, { statusToTone } from '../components/Badge';
import Pagination from '../components/Pagination';
import Modal from '../components/Modal';
import Button from '../components/Button';
import ConfirmDialog from '../components/ConfirmDialog';
import Toast, { ToastMessage } from '../components/Toast';
import axios from 'axios';
import { BaseUrl } from '../services/adminApi';


interface AdminUserRow {
  id: string;
  full_name: string;
  email: string;
  role: string;
  organisation?: string | null;
  verified: string;
  status: string;   
  date_joined: string;
}

const PAGE_SIZE = 10;

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function Users() {
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [selected, setSelected] = useState<AdminUserRow | null>(null);
  const [confirmUser, setConfirmUser] = useState<AdminUserRow | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const showToast = (text: string, type: ToastMessage['type'] = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Reset to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [search, roleFilter]);

  useEffect(() => {
  async function load() {
    setLoading(true);
    const params: Record<string, string | number> = {
      pagenum: page,
      pagesize: PAGE_SIZE,
      search: search || '',
    };
    if (roleFilter !== 'all') params.role = roleFilter;

    try {
      const res = await axios.get(BaseUrl + 'admin/users/', { params });
      const data = res.data;

      const results = Array.isArray(data) ? data : (data?.results ?? []);
      const count = Array.isArray(data) ? data.length : (data?.count ?? results.length);
      const pages = Array.isArray(data) ? 1 : (data?.total_pages ?? Math.max(1, Math.ceil(count / PAGE_SIZE)));

      setUsers(results);
      setTotalCount(count);
      setTotalPages(pages);
    } catch {
      setUsers([]);
      setTotalCount(0);
      setTotalPages(1);
      showToast('Could not load users.', 'error');
    } finally {
      setLoading(false);
    }
  }
  load();
}, [page, search, roleFilter]);

  // Client-side safety net in case the backend ignores the role param
  const filtered = roleFilter === 'all' ? users : users.filter(u => u.role === roleFilter);

  const isActive = (u: AdminUserRow) => u.status?.toLowerCase() === 'active';
  const isVerified = (u: AdminUserRow) => u.verified?.toLowerCase() === 'verified';

  const toggleActive = async (user: AdminUserRow) => {
    setActionLoading(true);
    const activating = !isActive(user);
    const endpoint = activating ? 'reactivate' : 'suspend';
    try {
      await axios.post(BaseUrl + `admin/users/${user.id}/${endpoint}/`);
      const nextStatus = activating ? 'Active' : 'Suspended';
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, status: nextStatus } : u));
      showToast(`${user.full_name} was ${activating ? 'reactivated' : 'suspended'}.`);
    } catch {
      showToast(`Could not ${activating ? 'reactivate' : 'suspend'} ${user.full_name}.`, 'error');
    } finally {
      setActionLoading(false);
      setConfirmUser(null);
    }
  };

  const columns: Column<AdminUserRow>[] = [
    {
      key: 'name', header: 'User', render: u => (
        <div>
          <p className="font-bold text-gray-900">{u.full_name}</p>
          <p className="text-[11px] text-gray-400">{u.email}</p>
        </div>
      ),
    },
    { key: 'role', header: 'Role', render: u => <span className="capitalize">{u.role}</span> },
    { key: 'org', header: 'Organisation', render: u => u.organisation || '—' },
    { key: 'verified', header: 'Verified', render: u => <Badge tone={isVerified(u) ? 'green' : 'amber'}>{isVerified(u) ? 'Verified' : 'Pending'}</Badge> },
    { key: 'status', header: 'Status', render: u => <Badge tone={statusToTone(isActive(u) ? 'active' : 'suspended')}>{isActive(u) ? 'Active' : 'Suspended'}</Badge> },
    { key: 'joined', header: 'Joined', render: u => formatDate(u.date_joined) },
    {
      key: 'actions', header: 'Action', align: 'right', render: u => (
        <button
          onClick={e => { e.stopPropagation(); setSelected(u); }}
          className="text-[12px] font-bold text-navy hover:underline"
        >
          View
        </button>
      ),
    },
  ];

  return (
    <div>
      <Toast message={toast} />
      <PageHeader title="Users" subtitle="Manage researcher, publisher, and institution accounts." />

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search by name or email…"
            className="w-full sm:w-64"
          />
          <select
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
            className="text-[13px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-navy"
          >
            <option value="all">All roles</option>
            <option value="researcher">Researcher</option>
            <option value="publisher">Publisher</option>
            <option value="institution">Institution</option>
          </select>
        </div>

        <DataTable columns={columns} rows={filtered} loading={loading} onRowClick={setSelected} emptyTitle="No users found" emptySubtitle="Try a different search term or filter." />
        <Pagination page={page} totalPages={totalPages} onChange={setPage} totalItems={totalCount ?? undefined} pageSize={PAGE_SIZE} />
      </Card>

      {/* User detail modal */}
      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        title="User details"
        footer={selected && (
          <>
            <Button variant="secondary" size="md" onClick={() => setSelected(null)}>Close</Button>
            <Button
              variant={isActive(selected) ? 'danger' : 'primary'}
              size="md"
              onClick={() => { setConfirmUser(selected); setSelected(null); }}
            >
              {isActive(selected) ? 'Suspend user' : 'Reactivate user'}
            </Button>
          </>
        )}
      >
        {selected && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-navy text-white flex items-center justify-center font-black text-[14px]">
                {selected?.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <p className="font-black text-gray-900 text-[14px]">{selected?.full_name}</p>
                <p className="text-[12px] text-gray-500">{selected.email}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              {[
                { label: 'Role', value: <span className="capitalize">{selected.role}</span> },
                { label: 'Organisation', value: selected.organisation || '—' },
                { label: 'Status', value: <Badge tone={statusToTone(isActive(selected) ? 'active' : 'suspended')}>{isActive(selected) ? 'Active' : 'Suspended'}</Badge> },
                { label: 'Verified', value: <Badge tone={isVerified(selected) ? 'green' : 'amber'}>{isVerified(selected) ? 'Yes' : 'No'}</Badge> },
                { label: 'Joined', value: formatDate(selected.date_joined) },
              ].map(f => (
                <div key={f.label}>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">{f.label}</p>
                  <div className="text-[13px] font-semibold text-gray-800">{f.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!confirmUser}
        onClose={() => setConfirmUser(null)}
        onConfirm={() => confirmUser && toggleActive(confirmUser)}
        title={confirmUser && isActive(confirmUser) ? 'Suspend user' : 'Reactivate user'}
        message={confirmUser
          ? `Are you sure you want to ${isActive(confirmUser) ? 'suspend' : 'reactivate'} ${confirmUser.full_name}? ${isActive(confirmUser) ? 'They will lose access to the platform immediately.' : 'They will regain access to the platform.'}`
          : ''}
        confirmLabel={confirmUser && isActive(confirmUser) ? 'Suspend' : 'Reactivate'}
        danger={!!confirmUser && isActive(confirmUser)}
        loading={actionLoading}
      />
    </div>
  );
}
